import { useCssModule, computed } from "vue";
import "./assets/index-CgZzOVq6.js";
import { u as useListeners } from "./assets/index-Ca8wHgNc.js";
import { c as camelize, u as useDataTestIdProp } from "./assets/index-BNd8MWnO.js";
import { u as useDisabledProp } from "./assets/index-DKxr0Yto.js";
import { u as useFullWidthProp } from "./assets/index-4vIud5LZ.js";
import { B as BaseLink, u as useLinkProps } from "./assets/BaseLink-KuovKwoP.js";
import { u as useLoadingProp } from "./assets/index-D0Qy8ijJ.js";
import "./assets/index-vzP2cezV.js";
import "vue-demi";
import "vue-router";
import "dompurify";
import "@vueuse/core";
import XSpinner from "./XSpinner.js";
import { n as normalizeComponent } from "./assets/_plugin-vue2_normalizer-VvYS6rEj.js";
import "vue-i18n";
import "./locales.js";
import "./assets/XIcon-U9bwokTN.js";
import "@fortawesome/vue-fontawesome";
const hexToHSL = (H) => {
  let r = 0;
  let g = 0;
  let b = 0;
  if (H.length === 4) {
    r = `0x${H[1]}${H[1]}`;
    g = `0x${H[2]}${H[2]}`;
    b = `0x${H[3]}${H[3]}`;
  } else if (H.length === 7) {
    r = `0x${H[1]}${H[2]}`;
    g = `0x${H[3]}${H[4]}`;
    b = `0x${H[5]}${H[6]}`;
  }
  r /= 255;
  g /= 255;
  b /= 255;
  const cmin = Math.min(r, g, b);
  const cmax = Math.max(r, g, b);
  const delta = cmax - cmin;
  let h = 0;
  let s = 0;
  let l = 0;
  if (delta === 0) h = 0;
  else if (cmax === r) h = (g - b) / delta % 6;
  else if (cmax === g) h = (b - r) / delta + 2;
  else h = (r - g) / delta + 4;
  h = Math.round(h * 60);
  if (h < 0) h += 360;
  l = (cmax + cmin) / 2;
  s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
  s = +(s * 100).toFixed(1);
  l = +(l * 100).toFixed(1);
  return `hsl(${h}, ${s}%, ${l}%)`;
};
const hslBrightnessRegex = /\d+\.?\d?%(?=\)$)/;
const getHSLBrightness = (hsl) => hslBrightnessRegex.exec(hsl)[0];
function replaceHSLBrightness(HSLColor, newLightness) {
  if (!newLightness || !HSLColor) return void 0;
  return HSLColor.replace(hslBrightnessRegex, newLightness);
}
const { getFullWidthClass } = useFullWidthProp();
const { getLoadingClass } = useLoadingProp();
function useClasses(props, hasCustomColors2) {
  const $style = useCssModule();
  const { loadingClass } = getLoadingClass(props);
  const customColorClassPrefix = computed(() => {
    return hasCustomColors2.value ? "custom" : "";
  });
  const invertedClassPrefix = computed(() => {
    return props.inverted ? "inverted" : "";
  });
  const modelClassName = computed(() => {
    return camelize(`model ${props.model ? props.model : "default"}`);
  });
  const designClassName = computed(() => {
    return `design ${props.design ? props.design : "default"}`;
  });
  const allClassName = computed(() => {
    const fullClassName = camelize(
      `${modelClassName.value} ${invertedClassPrefix.value} ${customColorClassPrefix.value} ${designClassName.value}`
    );
    const modelDesignClassName = camelize(
      `${modelClassName.value} ${invertedClassPrefix.value} ${customColorClassPrefix.value} design`
    );
    return {
      [$style[modelClassName.value]]: true,
      [$style[modelDesignClassName]]: true,
      [$style[fullClassName]]: true
    };
  });
  const isPlainModel = computed(() => props.model === "plain");
  const baseClass = computed(() => ({
    [$style.XButton]: !isPlainModel.value,
    [$style.XButtonPlain]: isPlainModel.value
  }));
  const sizeClass = computed(() => ({
    [$style.sizeNano]: props.size === "nano",
    [$style.sizeXSmall]: props.size === "xs",
    [$style.sizeSmall]: props.size === "sm",
    [$style.sizeMedium]: props.size === "md",
    [$style.sizeLarge]: props.size === "lg"
  }));
  const radiusClass = computed(() => ({
    [$style.radiusRounded]: props.borderRadius === "rounded",
    [$style.radiusCircle]: props.borderRadius === "circle",
    [$style.radiusSquare]: props.borderRadius === "square"
  }));
  const { fullWidthClass } = getFullWidthClass(props);
  const buttonClasses = computed(() => ({
    ...baseClass.value,
    ...allClassName.value,
    ...sizeClass.value,
    ...radiusClass.value,
    ...loadingClass.value,
    ...fullWidthClass.value
  }));
  return {
    buttonClasses
  };
}
const BASE_COLORS = {
  textColor: "#ffffff",
  // var(--color-neutral-0)
  color: "#5E0F2E"
  // var(--color-brand-secondary-400)
};
function useCustomColors(props) {
  const $style = useCssModule();
  const hasCustomColors2 = computed(() => !!(props.color || props.textColor));
  const primaryColorHEX = computed(() => props.color || BASE_COLORS.color);
  const primaryTextColorHEX = computed(() => props.textColor || BASE_COLORS.textColor);
  const primaryColorHSL = computed(() => hexToHSL(primaryColorHEX.value));
  const secondaryColorHSL = computed(() => hexToHSL(primaryTextColorHEX.value));
  const primaryColorLightness = computed(() => parseFloat(getHSLBrightness(primaryColorHSL.value)));
  function changePrimaryColorLightness(lightness) {
    return replaceHSLBrightness(
      primaryColorHSL.value,
      `${primaryColorLightness.value + lightness}%`
    );
  }
  const primaryColorsVariants = computed(() => ({
    light100: changePrimaryColorLightness(45),
    light200: changePrimaryColorLightness(30),
    dark500: changePrimaryColorLightness(-10)
  }));
  const customColorsVariables = computed(() => ({
    "--custom-primary-color": primaryColorHSL.value,
    "--custom-primary-color-100": primaryColorsVariants.value.light100,
    "--custom-primary-color-200": primaryColorsVariants.value.light200,
    "--custom-primary-color-500": primaryColorsVariants.value.dark500,
    "--custom-secondary-color": secondaryColorHSL.value
  }));
  const customColorsClass = computed(() => ({
    [$style.hasCustomColors]: hasCustomColors2.value
  }));
  return {
    hasCustomColors: hasCustomColors2,
    customColorsClass,
    customColorsVariables
  };
}
const XButton$1 = "_XButton_ajezc_1";
const XButtonPlain = "_XButtonPlain_ajezc_1";
const hasCustomColors = "_hasCustomColors_ajezc_21";
const isFullWidth = "_isFullWidth_ajezc_45";
const content = "_content_ajezc_48";
const loading = "_loading_ajezc_55";
const isDisabled = "_isDisabled_ajezc_62";
const radiusRounded = "_radiusRounded_ajezc_74";
const sizeSmall = "_sizeSmall_ajezc_77";
const sizeXSmall = "_sizeXSmall_ajezc_80";
const radiusCircle = "_radiusCircle_ajezc_85";
const sizeNano = "_sizeNano_ajezc_88";
const sizeLarge = "_sizeLarge_ajezc_99";
const radiusSquare = "_radiusSquare_ajezc_107";
const isLoading = "_isLoading_ajezc_123";
const modelDefaultDesignDefault = "_modelDefaultDesignDefault_ajezc_187";
const modelDefaultDesignText = "_modelDefaultDesignText_ajezc_195";
const modelDefaultDesignOutlined = "_modelDefaultDesignOutlined_ajezc_204";
const modelDefaultDesign = "_modelDefaultDesign_ajezc_187";
const modelDefaultInvertedDesign = "_modelDefaultInvertedDesign_ajezc_228";
const modelDefaultInvertedDesignDefault = "_modelDefaultInvertedDesignDefault_ajezc_232";
const modelDefaultInvertedDesignText = "_modelDefaultInvertedDesignText_ajezc_239";
const modelDefaultInvertedDesignOutlined = "_modelDefaultInvertedDesignOutlined_ajezc_247";
const modelAlternativeDesign = "_modelAlternativeDesign_ajezc_271";
const modelAlternativeDesignDefault = "_modelAlternativeDesignDefault_ajezc_276";
const modelAlternativeDesignText = "_modelAlternativeDesignText_ajezc_284";
const modelAlternativeDesignOutlined = "_modelAlternativeDesignOutlined_ajezc_293";
const modelAlternativeInvertedDesign = "_modelAlternativeInvertedDesign_ajezc_319";
const modelAlternativeInvertedDesignDefault = "_modelAlternativeInvertedDesignDefault_ajezc_324";
const modelAlternativeInvertedDesignText = "_modelAlternativeInvertedDesignText_ajezc_332";
const modelAlternativeInvertedDesignOutlined = "_modelAlternativeInvertedDesignOutlined_ajezc_341";
const modelLightDesignDefault = "_modelLightDesignDefault_ajezc_367";
const modelLightDesign = "_modelLightDesign_ajezc_367";
const modelDefaultCustomDesignDefault = "_modelDefaultCustomDesignDefault_ajezc_388";
const modelDefaultCustomDesignText = "_modelDefaultCustomDesignText_ajezc_393";
const modelDefaultCustomDesignOutlined = "_modelDefaultCustomDesignOutlined_ajezc_398";
const modelDefaultCustomDesign = "_modelDefaultCustomDesign_ajezc_388";
const modelDefaultInvertedCustomDesignDefault = "_modelDefaultInvertedCustomDesignDefault_ajezc_420";
const modelDefaultInvertedCustomDesignText = "_modelDefaultInvertedCustomDesignText_ajezc_425";
const modelDefaultInvertedCustomDesignOutlined = "_modelDefaultInvertedCustomDesignOutlined_ajezc_432";
const modelDefaultInvertedCustomDesign = "_modelDefaultInvertedCustomDesign_ajezc_420";
const style0 = {
  XButton: XButton$1,
  XButtonPlain,
  hasCustomColors,
  isFullWidth,
  content,
  loading,
  isDisabled,
  radiusRounded,
  sizeSmall,
  sizeXSmall,
  radiusCircle,
  sizeNano,
  sizeLarge,
  radiusSquare,
  isLoading,
  modelDefaultDesignDefault,
  modelDefaultDesignText,
  modelDefaultDesignOutlined,
  modelDefaultDesign,
  modelDefaultInvertedDesign,
  modelDefaultInvertedDesignDefault,
  modelDefaultInvertedDesignText,
  modelDefaultInvertedDesignOutlined,
  modelAlternativeDesign,
  modelAlternativeDesignDefault,
  modelAlternativeDesignText,
  modelAlternativeDesignOutlined,
  modelAlternativeInvertedDesign,
  modelAlternativeInvertedDesignDefault,
  modelAlternativeInvertedDesignText,
  modelAlternativeInvertedDesignOutlined,
  modelLightDesignDefault,
  modelLightDesign,
  modelDefaultCustomDesignDefault,
  modelDefaultCustomDesignText,
  modelDefaultCustomDesignOutlined,
  modelDefaultCustomDesign,
  modelDefaultInvertedCustomDesignDefault,
  modelDefaultInvertedCustomDesignText,
  modelDefaultInvertedCustomDesignOutlined,
  modelDefaultInvertedCustomDesign
};
const { createFullWidthProp } = useFullWidthProp();
const { createLinkProps } = useLinkProps();
const { createDisabledProp } = useDisabledProp();
const { createLoadingProp } = useLoadingProp();
const { createTestIdProp, setupTestId } = useDataTestIdProp();
const __default__ = {
  name: "XButton",
  components: { XSpinner, BaseLink }
};
const _sfc_main = /* @__PURE__ */ Object.assign(__default__, {
  props: {
    type: {
      type: String,
      default: "button",
      validator: (type) => /^$|button|submit/.test(type)
    },
    model: {
      type: String,
      default: "",
      validator: (model) => /^$|alternative|light|plain/.test(model)
    },
    design: {
      type: String,
      default: "",
      validator: (design) => /^$|outlined|text/.test(design)
    },
    inverted: {
      type: Boolean,
      default: false
    },
    borderRadius: {
      type: String,
      default: "rounded",
      validator: (borderRadius) => /^$|rounded|circle|square/.test(borderRadius)
    },
    size: {
      type: String,
      default: "",
      validator: (size) => /^$|nano|xs|sm|lg/.test(size)
    },
    color: {
      type: String,
      default: ""
    },
    textColor: {
      type: String,
      default: ""
    },
    ...createLinkProps(),
    ...createLoadingProp(),
    ...createDisabledProp(),
    ...createFullWidthProp(),
    ...createTestIdProp()
  },
  emits: ["click"],
  setup(__props) {
    const props = __props;
    const { v2Listeners } = useListeners(["click"]);
    const { formatTestId } = setupTestId(props);
    const { hasCustomColors: hasCustomColors2, customColorsClass, customColorsVariables } = useCustomColors(props);
    const { buttonClasses } = useClasses(props, hasCustomColors2);
    const componentName = computed(() => props.to ? "BaseLink" : "button");
    const buttonType = computed(() => props.to ? "" : props.type);
    return { __sfc: true, createFullWidthProp, createLinkProps, createDisabledProp, createLoadingProp, createTestIdProp, setupTestId, props, v2Listeners, formatTestId, hasCustomColors: hasCustomColors2, customColorsClass, customColorsVariables, buttonClasses, componentName, buttonType, XSpinner };
  }
});
var _sfc_render = function render() {
  var _vm = this, _c = _vm._self._c, _setup = _vm._self._setupProxy;
  return _c(_setup.componentName, _vm._g(_vm._b({ tag: "Component", class: [_setup.buttonClasses, _setup.customColorsClass], style: _setup.customColorsVariables, attrs: { "type": _setup.buttonType, "data-testid": _setup.formatTestId(), "disabled": _vm.disabled || _vm.loading }, on: { "click": ($event) => _vm.$emit("click", $event) } }, "Component", { ..._vm.$props, ..._vm.$attrs }, false), _setup.v2Listeners), [_c("div", { class: _vm.$style.content }, [_vm._t("default")], 2), _c("div", { class: _vm.$style.loading }, [_c(_setup.XSpinner, { directives: [{ name: "show", rawName: "v-show", value: _vm.loading, expression: "loading" }], staticStyle: { "--spinner-color": "inherit" }, attrs: { "data-testid": _setup.formatTestId("loading") } })], 1)]);
};
var _sfc_staticRenderFns = [];
const __cssModules = {
  "$style": style0
};
function _sfc_injectStyles(ctx) {
  for (var key in __cssModules) {
    this[key] = __cssModules[key];
  }
}
var __component__ = /* @__PURE__ */ normalizeComponent(
  _sfc_main,
  _sfc_render,
  _sfc_staticRenderFns,
  false,
  _sfc_injectStyles
);
const XButton = __component__.exports;
export {
  XButton as default
};
